import React, { useState, useEffect } from 'react';
import { getStartEndTime } from '../services/ApiService';
import Modal from './Modal.tsx';
import './WellSelection.css';

const WellSelection = ({ jobData, onWellSelected, setLoading }) => {
  const [selectedWells, setSelectedWells] = useState([]);
  const [wellsWithDetails, setWellsWithDetails] = useState([]);
  const [error, setError] = useState('');
  const [editingWell, setEditingWell] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  useEffect(() => {
    const loadWellDetails = async () => {
      if (!jobData) return;
      
      setLoading(true);
      try {
        let wells = [];
        
        console.log("Job data received:", jobData);
        
        // Check if jobData is an array (direct well data format)
        if (Array.isArray(jobData)) {
          console.log("Processing direct well data format");
          // Data is already in the expected format, just use it directly
          wells = jobData.map(well => {
            console.log("Processing well:", well);
            return {
              name: well.name,
              api: well.api,
              tvd: well.tvd || 0,
              clean: well.clean || 0,
              county: well.county || '',
              state: well.state || '',
              longitude: well.longitude || '',
              latitude: well.latitude || '',
              startDate: well.startDate || '',
              endDate: well.endDate || '',
              chemicals: well.chemicals || {},
              customer: well.customer || '' // Preserve customer field
            };
          });
        } 
        // Otherwise process the traditional format
        else if (jobData.wellData) {
          console.log("Processing traditional well data format");
          // Process well data in the traditional format
          for (const wellName in jobData.wellData) {
            const wellInfo = jobData.wellData[wellName];
            const coordInfo = jobData.coordData[wellName];
            
            // Find well API from coordinates data
            let wellApi = '';
            let longitude = '';
            let latitude = '';
            
            for (const api in jobData.wellCoordinates) {
              const coords = jobData.wellCoordinates[api];
              // Match well by name or other criteria
              wellApi = api;
              longitude = coords.longitude;
              latitude = coords.latitude;
              break; // For now, just take the first one
            }
            
            // Calculate TVD average
            let tvdAvg = 0;
            if (jobData.tvdPresent) {
              tvdAvg = (wellInfo.topTVDTotal + wellInfo.btmTVDTotal) / (2 * wellInfo.stageCount);
            }
            
            // Parse county and state from coordInfo
            let county = '';
            let state = '';
            
            if (coordInfo) {
              const parts = coordInfo.split(',');
              if (parts.length >= 2) {
                county = parts[0].replace(' County', '').trim();
                state = parts[1].trim();
                
                // Convert state abbreviation to full name if needed
                if (state.length === 2) {
                  state = getStateFullName(state);
                }
              }
            }
            
            // Get start and end times
            let startDate = '';
            let endDate = '';
            
            try {
              if (wellApi) {
                startDate = await getStartEndTime(wellApi, 1, 'timeStart');
                endDate = await getStartEndTime(wellApi, jobData.wellCoordinates[wellApi].totalStages, 'timeEnd');
              }
            } catch (error) {
              console.error(`Error getting start/end times for ${wellName}:`, error);
            }
            
            wells.push({
              name: wellName,
              api: wellApi,
              tvd: tvdAvg,
              clean: wellInfo.clean,
              county,
              state,
              longitude,
              latitude,
              startDate,
              endDate,
              chemicals: jobData.wellChemicalUsage[wellName] || {},
              customer: jobData.customer || wellInfo.customer || '' // Preserve customer field
            });
          }
        } else {
          console.error("Unrecognized job data format:", jobData);
          setError("Unrecognized job data format");
        }
        
        setWellsWithDetails(wells);
      } catch (error) {
        console.error('Error loading well details:', error);
        setError('Failed to load well details: ' + error.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadWellDetails();
  }, [jobData, setLoading]);

  const handleWellToggle = (wellName) => {
    setSelectedWells(prev => {
      if (prev.includes(wellName)) {
        return prev.filter(name => name !== wellName);
      } else {
        return [...prev, wellName];
      }
    });
  };

  const handleEditWell = (well, event) => {
    event.stopPropagation(); // Prevent well selection toggle
    setEditingWell(well);
    setEditFormData({
      name: well.name,
      api: well.api || '',
      county: well.county || '',
      state: well.state || '',
      tvd: well.tvd || 0,
      clean: well.clean || 0,
      startDate: well.startDate || '',
      endDate: well.endDate || '',
      longitude: well.longitude || '',
      latitude: well.latitude || ''
    });
  };

  const handleSaveEdit = () => {
    // Update the well in wellsWithDetails
    setWellsWithDetails(prev => prev.map(well => 
      well.name === editingWell.name 
        ? {
            ...well,
            ...editFormData,
            tvd: parseFloat(editFormData.tvd) || 0,
            clean: parseFloat(editFormData.clean) || 0
          }
        : well
    ));
    
    // Close the modal
    setEditingWell(null);
    setEditFormData({});
  };

  const handleCancelEdit = () => {
    setEditingWell(null);
    setEditFormData({});
  };

  const handleFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleContinue = () => {
    if (selectedWells.length === 0) {
      setError('Please select at least one well to continue');
      return;
    }
    
    // Find selected well details
    const selectedWellDetails = wellsWithDetails.filter(well => 
      selectedWells.includes(well.name)
    );
    
    // Log the selected well details for debugging
    console.log("Selected well details:", selectedWellDetails);
    
    // Process all selected wells
    const processedWells = selectedWellDetails.map(well => {
      const processedWell = { ...well };
      
      // Ensure totalClean is set (it might be called 'clean' in the data)
      if (!processedWell.totalClean && processedWell.clean) {
        processedWell.totalClean = processedWell.clean;
      }
      
      // Ensure chemicals is an object
      if (!processedWell.chemicals) {
        processedWell.chemicals = {};
      }
      
      // Map property names to match what XmlGenerator expects
      processedWell.wellName = processedWell.name;
      processedWell.apiNumber = processedWell.api;
      
      // Ensure customer field is preserved
      if (!processedWell.customer) {
        console.log("Customer field is missing, setting to empty string");
        processedWell.customer = '';
      } else {
        console.log(`Customer field is set to: ${processedWell.customer}`);
      }
      
      return processedWell;
    });
    
    console.log("Processed wells data:", processedWells);
    
    // Pass all selected wells to the parent component
    onWellSelected(processedWells);
  };

  return (
    <div className="well-selection">
      <div className="well-selection-header">
        <h2>Select Wells</h2>
        <p>Choose one or more wells to generate XML files for</p>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="wells-list">
        {wellsWithDetails.length === 0 ? (
          <div className="no-wells">No wells found for this job number</div>
        ) : (
          wellsWithDetails.map(well => (
            <div 
              key={well.name} 
              className={`well-card ${selectedWells.includes(well.name) ? 'selected' : ''}`}
              onClick={() => handleWellToggle(well.name)}
            >
              <div className="well-card-header">
                <h3>{well.name}</h3>
                <div className="well-card-actions">
                  <button 
                    className="edit-button"
                    onClick={(e) => handleEditWell(well, e)}
                    title="Edit well details"
                  >
                    Edit
                  </button>
                  <div className="well-checkbox">
                    <input 
                      type="checkbox" 
                      checked={selectedWells.includes(well.name)}
                      onChange={() => {}} // Controlled component
                      onClick={(e) => {
                        e.stopPropagation();
                        handleWellToggle(well.name);
                      }}
                    />
                  </div>
                </div>
              </div>
              
              <div className="well-details">
                <div className="well-detail">
                  <span className="detail-label">API:</span>
                  <span className="detail-value">{well.api || 'N/A'}</span>
                </div>
                <div className="well-detail">
                  <span className="detail-label">County:</span>
                  <span className="detail-value">{well.county || 'N/A'}</span>
                </div>
                <div className="well-detail">
                  <span className="detail-label">State:</span>
                  <span className="detail-value">{well.state || 'N/A'}</span>
                </div>
                <div className="well-detail">
                  <span className="detail-label">TVD (avg):</span>
                  <span className="detail-value">{well.tvd ? well.tvd : 'N/A'}</span>
                </div>
                <div className="well-detail">
                  <span className="detail-label">Clean Volume:</span>
                  <span className="detail-value">{well.clean ? well.clean : 'N/A'} gals</span>
                </div>
                <div className="well-detail">
                  <span className="detail-label">Start Date:</span>
                  <span className="detail-value">{well.startDate || 'N/A'}</span>
                </div>
                <div className="well-detail">
                  <span className="detail-label">End Date:</span>
                  <span className="detail-value">{well.endDate || 'N/A'}</span>
                </div>
                <div className="well-detail">
                  <span className="detail-label">Chemicals:</span>
                  <span className="detail-value">{Object.keys(well.chemicals || {}).length} items</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      
      <div className="well-selection-actions">
        <button 
          className="continue-button"
          onClick={handleContinue}
          disabled={selectedWells.length === 0}
        >
          Continue with Selected Wells ({selectedWells.length})
        </button>
      </div>

      {/* Edit Well Modal */}
      <Modal
        isOpen={!!editingWell}
        onClose={handleCancelEdit}
        title={`Edit Well: ${editingWell?.name || ''}`}
        footer={
          <div className="edit-modal-footer">
            <button className="cancel-button" onClick={handleCancelEdit}>
              Cancel
            </button>
            <button className="save-button" onClick={handleSaveEdit}>
              Save Changes
            </button>
          </div>
        }
      >
        {editingWell && (
          <div className="edit-well-form">
            <div className="form-row">
              <div className="form-group">
                <label>Well Name:</label>
                <input
                  type="text"
                  value={editFormData.name || ''}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  disabled
                  className="form-input disabled"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>API:</label>
                <input
                  type="text"
                  value={editFormData.api || ''}
                  onChange={(e) => handleFormChange('api', e.target.value)}
                  className="form-input"
                  placeholder="42-461-42471-00-00"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>County:</label>
                <input
                  type="text"
                  value={editFormData.county || ''}
                  onChange={(e) => handleFormChange('county', e.target.value)}
                  className="form-input"
                  placeholder="Upton"
                />
              </div>
              <div className="form-group">
                <label>State:</label>
                <input
                  type="text"
                  value={editFormData.state || ''}
                  onChange={(e) => handleFormChange('state', e.target.value)}
                  className="form-input"
                  placeholder="TX"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>TVD (avg):</label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.tvd || ''}
                  onChange={(e) => handleFormChange('tvd', e.target.value)}
                  className="form-input"
                  placeholder="9468.12"
                />
              </div>
              <div className="form-group">
                <label>Clean Volume (gals):</label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.clean || ''}
                  onChange={(e) => handleFormChange('clean', e.target.value)}
                  className="form-input"
                  placeholder="22899072.00"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Start Date:</label>
                <input
                  type="text"
                  value={editFormData.startDate || ''}
                  onChange={(e) => handleFormChange('startDate', e.target.value)}
                  className="form-input"
                  placeholder="2024-08-05 11:09"
                />
              </div>
              <div className="form-group">
                <label>End Date:</label>
                <input
                  type="text"
                  value={editFormData.endDate || ''}
                  onChange={(e) => handleFormChange('endDate', e.target.value)}
                  className="form-input"
                  placeholder="2024-08-15 21:58"
                />
              </div>
            </div>

            <div className="form-note">
              <p><strong>Note:</strong> Chemical count is automatically calculated and cannot be edited here.</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// Helper function to convert state abbreviation to full name
const getStateFullName = (abbr) => {
  const states = {
    'AL': 'Alabama',
    'AK': 'Alaska',
    'AZ': 'Arizona',
    'AR': 'Arkansas',
    'CA': 'California',
    'CO': 'Colorado',
    'CT': 'Connecticut',
    'DE': 'Delaware',
    'FL': 'Florida',
    'GA': 'Georgia',
    'HI': 'Hawaii',
    'ID': 'Idaho',
    'IL': 'Illinois',
    'IN': 'Indiana',
    'IA': 'Iowa',
    'KS': 'Kansas',
    'KY': 'Kentucky',
    'LA': 'Louisiana',
    'ME': 'Maine',
    'MD': 'Maryland',
    'MA': 'Massachusetts',
    'MI': 'Michigan',
    'MN': 'Minnesota',
    'MS': 'Mississippi',
    'MO': 'Missouri',
    'MT': 'Montana',
    'NE': 'Nebraska',
    'NV': 'Nevada',
    'NH': 'New Hampshire',
    'NJ': 'New Jersey',
    'NM': 'New Mexico',
    'NY': 'New York',
    'NC': 'North Carolina',
    'ND': 'North Dakota',
    'OH': 'Ohio',
    'OK': 'Oklahoma',
    'OR': 'Oregon',
    'PA': 'Pennsylvania',
    'RI': 'Rhode Island',
    'SC': 'South Carolina',
    'SD': 'South Dakota',
    'TN': 'Tennessee',
    'TX': 'Texas',
    'UT': 'Utah',
    'VT': 'Vermont',
    'VA': 'Virginia',
    'WA': 'Washington',
    'WV': 'West Virginia',
    'WI': 'Wisconsin',
    'WY': 'Wyoming'
  };
  
  return states[abbr.toUpperCase()] || abbr;
};

export default WellSelection;
